import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import validateConditions from './validateConditions';

/**
 * Determines whether a schema node (tab, section, object, ...) should be
 * visible for the given form data.
 *
 * Mirrors the hide semantics of ConditionalAssert (see
 * components/form-viewer/object-types/conditional.js): a node configured with
 * `behavior: 'hide'` is visible only when its conditions are met
 * (validateConditions === true). A node with no conditions/rules is always
 * visible. A `disable` behavior is a no-op here, so the node stays visible.
 *
 * @param {Object} node - schema node (may carry a `conditions` object)
 * @param {Object} data - current form data (selectedForm.data)
 * @returns {boolean} true when the node should be rendered/navigable
 */
export const isNodeVisible = (node = {}, data = {}) => {
  const { conditions = {} } = node || {};
  const { rules = [], behavior = 'hide' } = conditions;

  if (isEmpty(conditions) || rules.length === 0) {
    return true;
  }

  const shouldShow = validateConditions(conditions, data, data, 0);

  return behavior === 'hide' ? shouldShow : true;
};

// A tab is just a top-level schema node — kept as a named alias for the
// navigation code (tabs-header/tabs-content).
export const isTabVisible = isNodeVisible;

/**
 * Returns the original indexes of the tabs that should be visible. Index
 * integrity is preserved: hidden tabs are omitted from the result, the
 * remaining indexes are NOT renumbered.
 *
 * @param {Array} properties - the tabs array
 * @param {Object} data - current form data
 * @returns {number[]} visible tab indexes, in original order
 */
export const getVisibleTabIndexes = (properties = [], data = {}) =>
  properties.reduce((visible, tab, index) => {
    if (isTabVisible(tab, data)) {
      visible.push(index);
    }
    return visible;
  }, []);

/**
 * Given the list of visible tab indexes, returns the next visible index after
 * `current`, or `undefined` when there is no visible tab ahead. Used to drive
 * Next-button visibility and skip-hidden navigation.
 *
 * @param {number[]} visibleIndexes - visible tab indexes (original order)
 * @param {number} current - the active tab index
 * @returns {number|undefined}
 */
export const getNextVisibleTabIndex = (visibleIndexes = [], current = 0) =>
  visibleIndexes.find(index => index > current);

/**
 * Given the list of visible tab indexes, returns the previous visible index
 * before `current`, or `undefined` when there is no visible tab behind.
 *
 * @param {number[]} visibleIndexes - visible tab indexes (original order)
 * @param {number} current - the active tab index
 * @returns {number|undefined}
 */
export const getPrevVisibleTabIndex = (visibleIndexes = [], current = 0) => {
  const before = visibleIndexes.filter(index => index < current);
  return before.length ? before[before.length - 1] : undefined;
};

/**
 * Returns the target index for a "Next" navigation action, or `undefined`
 * when the current tab is already the last navigable one.
 *
 * When conditional tabs are active (`visibleTabIndexes` is non-empty) the
 * result skips hidden tabs via `getNextVisibleTabIndex`. Otherwise it falls
 * back to plain `current + 1`, clamped so it never exceeds `totalTabs - 1`.
 *
 * @param {number[]} visibleTabIndexes - visible tab indexes in original order
 * @param {number} current - the currently active tab index
 * @param {number} totalTabs - total number of tabs (for fallback clamping)
 * @returns {number|undefined}
 */
export const getNextTabTarget = (
  visibleTabIndexes = [],
  current = 0,
  totalTabs = 0
) => {
  if (visibleTabIndexes.length) {
    return getNextVisibleTabIndex(visibleTabIndexes, current);
  }
  const next = current + 1;
  return next < totalTabs ? next : undefined;
};

/**
 * Returns the target index for a "Prev" navigation action, or `undefined`
 * when the current tab is already the first navigable one.
 *
 * When conditional tabs are active (`visibleTabIndexes` is non-empty) the
 * result skips hidden tabs via `getPrevVisibleTabIndex`. Otherwise it falls
 * back to plain `current - 1`, clamped so it never goes below 0.
 *
 * @param {number[]} visibleTabIndexes - visible tab indexes in original order
 * @param {number} current - the currently active tab index
 * @returns {number|undefined}
 */
export const getPrevTabTarget = (visibleTabIndexes = [], current = 0) => {
  if (visibleTabIndexes.length) {
    return getPrevVisibleTabIndex(visibleTabIndexes, current);
  }
  return current > 0 ? current - 1 : undefined;
};

/**
 * Walks the tabs schema (`formSchema.form.properties`) to determine whether the
 * section identified by `sectionId` is currently visible: the section is hidden
 * if its tab — or any ancestor container with hide-conditions — is hidden.
 *
 * @param {string} sectionId - schema node id (matches __assignedSections.sectionId)
 * @param {Array} properties - the tabs array
 * @param {Object} data - current form data
 * @returns {boolean} true when the section is visible (or cannot be located)
 */
export const isSchemaSectionVisible = (
  sectionId,
  properties = [],
  data = {}
) => {
  const walk = (nodes, ancestorsVisible) => {
    for (const node of nodes || []) {
      if (!node) continue;

      const nodeVisible = ancestorsVisible && isNodeVisible(node, data);

      if (node.id === sectionId) {
        return { found: true, visible: nodeVisible };
      }

      if (Array.isArray(node.properties)) {
        const result = walk(node.properties, nodeVisible);
        if (result.found) {
          return result;
        }
      }
    }
    return { found: false, visible: true };
  };

  const { found, visible } = walk(properties, true);

  // Never hide a section we cannot locate in the schema.
  return found ? visible : true;
};

/**
 * Of the assigned (CTR) sections, returns only those that should still gate the
 * submit button: incomplete AND currently visible. Hidden CTR sections are
 * excluded so they don't downgrade/block submit.
 *
 * @param {Array} assignedSections - selectedForm.data.__assignedSections
 * @param {Array} properties - the tabs array (formSchema.form.properties)
 * @param {Object} data - current form data
 * @returns {Array} incomplete + visible assigned sections
 */
export const getBlockingAssignedSections = (
  assignedSections = [],
  properties = [],
  data = {}
) =>
  (assignedSections || []).filter(
    section =>
      section &&
      !section.complete &&
      isSchemaSectionVisible(section.sectionId, properties, data)
  );

const isFieldRequired = validationRules => {
  if (Array.isArray(validationRules)) {
    return validationRules.some(rule => rule && (rule.required || rule.mustExist));
  }
  return !!(
    validationRules &&
    (validationRules.required || validationRules.mustExist)
  );
};

// Conservative emptiness: only scalar blanks count. Numbers (0), booleans
// (false), arrays and objects are NOT treated as empty, so a genuinely-filled
// field is never flagged (avoids wrongly downgrading a valid submit).
const isBlank = value =>
  value === undefined || value === null || value === '';

/**
 * True if any VISIBLE tab has a required field whose conditions are met and
 * whose value is blank. Conservative by design (mirrors getTabFieldsError's
 * required/condition logic, see validations.js) so a complete form is never
 * wrongly downgraded to "Save Draft".
 *
 * NOTE: like isSchemaSectionVisible, field conditions are evaluated against the
 * whole form data at parentIndex 0 — repeater per-item required fields are not
 * deeply covered (acceptable for top-level fields).
 *
 * @param {Object} selectedForm - the Redux selectedForm (data + formFields)
 * @param {number[]} visibleTabIndexes - currently visible tab indexes
 * @returns {boolean}
 */
export const hasIncompleteRequiredFieldsInVisibleTabs = (
  selectedForm = {},
  visibleTabIndexes = []
) => {
  const data = selectedForm.data || {};
  const tabs = get(selectedForm, ['formFields', 'fields'], []);

  return (visibleTabIndexes || []).some(tabIndex => {
    const tabFields = tabs[tabIndex] || {};
    return Object.keys(tabFields).some(fieldKey => {
      const { conditions = {}, validationRules } = tabFields[fieldKey] || {};

      if (!isFieldRequired(validationRules)) {
        return false;
      }

      // Skip fields hidden by their own conditions.
      const visible =
        isEmpty(conditions) || validateConditions(conditions, data, data, 0);
      if (!visible) {
        return false;
      }

      return isBlank(get(data, fieldKey));
    });
  });
};
